import React, { useMemo, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'

// styles
import '@aytm/lib/rfd-ui/styles/basic/scroll-bar.scss'

const CLASS = 'rfd-ui-scroll-bar'

const inRect = (event, rect, margins, ignoreClasses) => {
  if (!event || !rect) return false
  if (ignoreClasses && ignoreClasses.length) {
    const className = event && event.target ? event.target.className : null
    if (className && className.includes) {
      for (const testClass of ignoreClasses) {
        if (className.includes(testClass)) {
          return false
        }
      }
    }
  }
  const m = {
    top: margins?.top || 0,
    left: margins?.left || 0,
    right: margins?.right || 0,
    bottom: margins?.bottom || 0
  }
  return (
    event.clientX >= rect.left + m.left &&
    event.clientX <= rect.right - m.right &&
    event.clientY >= rect.top + m.top &&
    event.clientY <= rect.bottom - m.bottom
  )
}

const Scroll = ({
  listener: eventListener,
  button,
  element,
  direction = 'ver',
  minButtonSize = 32,
  wheelStep = 32,
  clickStep = 32,
  wheelEvents = 'none',
  ignoreClasses = null,
  margins = {}
} = {}) => {
  let mounted = false
  let move = 0
  let position = 0
  let startPoint = 0
  let scrollEvent = null
  let source = 'mouse'
  let showScroll = true
  let buttonSize = 0
  let barSize = 0

  const listener = eventListener || document
  const container = element?.parentElement
  const horizontal = 'hor' === direction.slice(0, 3)
  const scrollBar = button?.parentElement

  let containerRect = null
  let barRect = null

  const handleScroll = () => {
    if (mounted && scrollEvent) {
      if (horizontal)
        scrollEvent({
          scrollLeft: container.scrollLeft,
          scrollPosition: container.scrollLeft,
          scrollSize: container.scrollWidth,
          horizontal: true
        })
      else
        scrollEvent({
          scrollTop: container.scrollTop,
          scrollPosition: container.scrollTop,
          scrollSize: container.scrollHeight,
          horizontal: false
        })
    }
  }

  const scrollElement = () => {
    if (mounted && container && button && barRect) {
      const [scrollSize, barSize] = horizontal
        ? [container.scrollWidth, barRect.width]
        : [container.scrollHeight, barRect.height]
      const scrollPos = Math.round((position * scrollSize) / barSize)
      if (horizontal) container.scrollLeft = scrollPos
      else container.scrollTop = scrollPos
      handleScroll()
    }
  }

  const stepBy = step => {
    if (mounted && button && barRect) {
      const buttonPos = horizontal ? button.offsetLeft : button.offsetTop
      const moveTo = buttonPos + step
      const right = barSize - buttonSize
      const newPosition = moveTo < 0 ? 0 : moveTo > right ? right : moveTo
      if (newPosition !== position) {
        position = newPosition
        if (horizontal) button.style.left = `${position}px`
        else button.style.top = `${position}px`
        scrollElement()
      }
    }
  }

  const scrollTo = to => {
    if (mounted && !move && container && button) {
      if (horizontal) {
        container.scrollLeft = to
        handleScroll()
      } else {
        container.scrollTop = to
        handleScroll()
      }
    }
  }

  const scrollBy = step => {
    if (mounted && container) {
      if (horizontal) {
        container.scrollLeft = container.scrollLeft + step
        handleScroll()
      } else {
        container.scrollTop = container.scrollTop + step
        handleScroll()
      }
    }
  }

  const onMouseUp = () => {
    move = 0
  }

  const onMouseLeave = () => {
    move = 0
  }

  const onMouseDown = (event, touch) => {
    if (mounted && button) {
      const [clientX, clientY] = touch
        ? [event.touches[0].clientX, event.touches[0].clientY]
        : [event.clientX, event.clientY]
      const buttonRect = button.getBoundingClientRect()
      if (inRect({ clientX, clientY }, buttonRect)) {
        move = 1
        startPoint = horizontal ? clientX : clientY
      } else if (inRect({ clientX, clientY }, barRect)) {
        if (horizontal) {
          if (clientX < buttonRect.left) stepBy(-clickStep)
          else if (clientX > buttonRect.right) stepBy(clickStep)
        } else {
          if (clientY < buttonRect.top) stepBy(-clickStep)
          else if (clientY >= buttonRect.bottom) stepBy(clickStep)
        }
      } else if (touch && inRect({ clientX, clientY }, containerRect)) {
        move = 2
        startPoint = horizontal ? clientX : clientY
        event.stopPropagation()
      }
    }
  }

  const onMouseMove = (event, touch) => {
    if (mounted && move && button) {
      const [clientX, clientY] = touch
        ? [event.touches[0].clientX, event.touches[0].clientY]
        : [event.clientX, event.clientY]
      if (touch && 2 === move) {
        const pointPos = horizontal ? clientX : clientY
        const step = startPoint - pointPos
        scrollBy(step)
        startPoint = pointPos
      } else if (barRect) {
        const [pointPos, buttonPos] = horizontal ? [clientX, button.offsetLeft] : [clientY, button.offsetTop]
        const moveTo = buttonPos + pointPos - startPoint
        const right = barSize - buttonSize
        const newPosition = moveTo < 0 ? 0 : moveTo > right ? right : moveTo
        if (newPosition !== position) {
          position = newPosition
          startPoint = pointPos
          if (horizontal) button.style.left = `${position}px`
          else button.style.top = `${position}px`
          scrollElement()
        }
      }
    }
  }

  const onMouseWheel = event => {
    if (mounted && showScroll) {
      containerRect = container ? container.getBoundingClientRect() : null
      if (event.deltaX > 0) source = 'pad'
      if ((wheelEvents || '').includes('shift') && event.shiftKey && container) {
        if (inRect(event, containerRect)) {
          const step = Math.round(0.01 * event.deltaY * wheelStep)
          stepBy(step)
          event.preventDefault()
          return false
        }
      } else {
        if ((wheelEvents || '').includes('element') && container) {
          // eslint-disable-next-line unicorn/no-lonely-if
          if (inRect(event, containerRect, margins.current, ignoreClasses)) {
            if ('mouse' === source) {
              const delta = event.deltaY
              const step = Math.round(0.01 * delta * wheelStep)
              stepBy(step)
              event.preventDefault()
              return false
            } else {
              if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
                const delta = event.deltaX
                const step = Math.round(0.01 * delta * wheelStep)
                stepBy(step)
                event.preventDefault()
                return false
              }
            }
          }
        }
        if ((wheelEvents || '').includes('bar') && button) {
          // eslint-disable-next-line unicorn/no-lonely-if
          barRect = scrollBar ? scrollBar.getBoundingClientRect() : null
          if (inRect(event, barRect)) {
            const step = Math.round(0.01 * event.deltaY * wheelStep)
            stepBy(step)
            event.preventDefault()
            return false
          }
        }
      }
    }
    return true
  }

  const onTouchStart = event => {
    onMouseDown(event, true)
  }

  const onTouchEnd = () => {
    onMouseUp()
  }

  const onTouchMove = event => {
    onMouseMove(event, true)
  }

  const updateBar = () => {
    if (mounted && container && button && element) {
      const [scrollSize, containerSize, scrollPos, elementSize] = horizontal
        ? [container.scrollWidth, container.clientWidth, container.scrollLeft, element.clientWidth]
        : [container.scrollHeight, container.clientHeight, container.scrollTop, element.clientHeight]
      showScroll = containerSize < elementSize
      if (showScroll) {
        if ('none' === button.parentElement.style.display) button.parentElement.style.display = 'flex'
        if ('none' === button.style.display) button.style.display = 'flex'
        barRect = scrollBar ? scrollBar.getBoundingClientRect() : null
        barSize = horizontal ? barRect.width : barRect.height
        buttonSize = Math.floor((barSize * containerSize) / scrollSize)
        const size = buttonSize < minButtonSize ? minButtonSize : buttonSize >= barSize ? 0 : buttonSize
        const position = Math.floor((scrollPos * barSize) / scrollSize)
        if (size > 0) {
          if (horizontal) {
            button.style.width = `${size}px`
            button.style.left = `${position}px`
          } else {
            button.style.height = `${size}px`
            button.style.top = `${position}px`
          }
        }
      } else {
        if ('none' !== button.style.display || 'none' !== button.parentElement.style.display) scrollTo(0)
        if ('none' !== button.style.display) button.style.display = 'none'
        if ('none' !== button.parentElement.style.display) button.parentElement.style.display = 'none'
        barRect = scrollBar ? scrollBar.getBoundingClientRect() : null
      }
    }
  }

  const onResize = () => {
    updateBar()
    handleScroll()
  }

  const bind = ({ onScroll }) => {
    if (scrollEvent !== onScroll) scrollEvent = onScroll
    mounted = true
    updateBar()
  }

  let observer = null
  if (element && container) {
    observer = new ResizeObserver(() => onResize())
    observer.observe(element)
    observer.observe(container)
  }

  const unbind = () => {
    mounted = false
    if (observer) observer.disconnect()
    listener.removeEventListener('mouseup', onMouseUp)
    listener.removeEventListener('mousedown', onMouseDown)
    listener.removeEventListener('mousemove', onMouseMove)
    listener.removeEventListener('mouseleave', onMouseLeave)
    listener.removeEventListener('wheel', onMouseWheel)
    listener.removeEventListener('touchstart', onTouchStart)
    listener.removeEventListener('touchend', onTouchEnd)
    listener.removeEventListener('touchmove', onTouchMove)
  }

  listener.addEventListener('mouseup', onMouseUp, { passive: false })
  listener.addEventListener('mousedown', onMouseDown, { passive: false })
  listener.addEventListener('mousemove', onMouseMove, { passive: false })
  listener.addEventListener('mouseleave', onMouseLeave, { passive: false })
  listener.addEventListener('wheel', onMouseWheel, { passive: false })
  listener.addEventListener('touchstart', onTouchStart, { passive: false })
  listener.addEventListener('touchend', onTouchEnd, { passive: false })
  listener.addEventListener('touchmove', onTouchMove, { passive: false })

  containerRect = container ? container.getBoundingClientRect() : null
  barRect = scrollBar ? scrollBar.getBoundingClientRect() : null

  return {
    unbind,
    bind,
    scrollTo,
    updateBar
  }
}

const ScrollBar = props => {
  const scrollRef = useRef()
  const buttonRef = useRef()

  const margins = useRef()
  margins.current = props.margins

  const paddings = useRef()
  paddings.current = props.paddings

  const direction = useMemo(() => props.direction?.slice(0, 3), [props.direction])
  const ignoreClasses = useMemo(
    () =>
      Array.isArray(props.ignoreClasses)
        ? props.ignoreClasses
        : 'string' === typeof props.ignoreClasses
        ? props.ignoreClasses.split(' ')
        : [],
    [props.ignoreClasses]
  )

  useEffect(() => {
    const listener = props.listenerRef?.current
    const button = buttonRef.current
    const element = props.elementRef?.current
    scrollRef.current = Scroll({
      listener,
      button,
      element,
      direction,
      minButtonSize: props.minButtonSize,
      clickStep: props.clickStep,
      wheelStep: props.wheelStep,
      wheelEvents: props.wheelEvents,
      ignoreClasses: ignoreClasses.length ? ignoreClasses : null,
      margins,
      paddings
    })
    return () => scrollRef.current.unbind()
  }, [
    direction,
    ignoreClasses,
    props.clickStep,
    props.elementRef,
    props.listenerRef,
    props.minButtonSize,
    props.wheelEvents,
    props.wheelStep
  ])

  useEffect(() => {
    if (scrollRef && scrollRef.current) scrollRef.current.bind({ onScroll: props.onScroll })
  }, [props.onScroll, scrollRef])

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      scrollRef.current.scrollTo(props.scrollPosition)
      scrollRef.current.updateBar()
    }
  }, [props.scrollPosition, scrollRef])

  return (
    <div className={`${CLASS}-${direction} ${props.className || ''}`}>
      <div className={`${CLASS}-${direction}-button`} draggable={'false'} ref={buttonRef}></div>
    </div>
  )
}

ScrollBar.propTypes = {
  className: PropTypes.string,
  direction: PropTypes.string,
  listenerRef: PropTypes.object,
  elementRef: PropTypes.object,
  minButtonSize: PropTypes.number,
  wheelStep: PropTypes.number,
  clickStep: PropTypes.number,
  wheelEvents: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  ignoreClasses: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  scrollPosition: PropTypes.number,
  margins: PropTypes.object,
  paddings: PropTypes.object,
  onScroll: PropTypes.func
}

ScrollBar.defaultProps = {
  direction: 'hor',
  minButtonSize: 32,
  wheelStep: 32,
  clickStep: 32,
  wheelEvents: 'bar shift',
  scrollPosition: 0,
  margins: {},
  paddings: { left: 100, right: 100 }
}

export default ScrollBar
